import { useState, useEffect, useCallback } from "react";
import { useApi } from "./AuthContext";
import {
  Image, Plus, Trash2, X, Check, GripVertical, Upload,
  MapPin, Calendar, Briefcase, ChevronLeft, ChevronRight,
  AlertTriangle, Info, Eye
} from "lucide-react";
import { toast } from "sonner";
import BackButton from "./BackButton";

const EMPTY_FORM = {
  title: "", description: "", category: "residential",
  work_type: "", location: "", date: "", priority: 99,
};

export default function PortfolioModule() {
  const api = useApi();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [previewProject, setPreviewProject] = useState(null);
  const [previewIdx, setPreviewIdx] = useState(0);
  const [photoUrls, setPhotoUrls] = useState({});
  const [dragPhoto, setDragPhoto] = useState(null); // { projectId, fromIndex }
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [unsavedReorder, setUnsavedReorder] = useState({}); // { projectId: [newPhotosArray] }

  const fetchProjects = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/portfolio");
      setProjects(data);
    } catch (err) { toast.error("Failed to load portfolio"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const resetForm = () => { setForm(EMPTY_FORM); setEditingProject(null); setShowForm(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await api.put(`/admin/portfolio/${editingProject.id}`, form);
        toast.success("Project updated");
      } else {
        await api.post("/admin/portfolio", form);
        toast.success("Project created");
      }
      resetForm(); fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleEdit = (p) => {
    setEditingProject(p);
    setForm({
      title: p.title, description: p.description || "",
      category: p.category || "residential", work_type: p.work_type || "",
      location: p.location || "", date: p.date || "",
      priority: p.priority ?? 99,
    });
    setShowForm(true);
  };

  const handleDelete = (p) => {
    setConfirmModal({
      title: "Delete this project?",
      message: `"${p.title}" and all its photos will be permanently removed from the site.`,
      danger: true,
      onConfirm: async () => {
        try { await api.delete(`/admin/portfolio/${p.id}`); toast.success("Project deleted"); fetchProjects(); }
        catch (err) { toast.error("Failed to delete"); }
        setConfirmModal(null);
      }
    });
  };

  const handlePriorityChange = (p, newPriority) => {
    setConfirmModal({
      title: "Change display order?",
      message: `Move "${p.title}" to position #${newPriority}. This will change the order on the public website.`,
      onConfirm: async () => {
        try {
          await api.put(`/admin/portfolio/${p.id}`, { priority: Number(newPriority) });
          toast.success(`Priority updated to #${newPriority}`);
          fetchProjects();
        } catch (err) { toast.error("Failed to update priority"); }
        setConfirmModal(null);
      }
    });
  };

  const handlePhotoUpload = async (projectId, files) => {
    if (!files?.length) return;
    setUploading(true);
    let count = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/admin/portfolio/${projectId}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        count++;
      } catch (err) {
        if (err.response?.status === 400) { toast.error("Max 10 photos reached"); break; }
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (count > 0) { toast.success(`${count} photo(s) uploaded`); fetchProjects(); }
    setUploading(false);
  };

  const handleDeletePhoto = (projectId, photoUrl) => {
    setConfirmModal({
      title: "Remove this photo?",
      message: "This photo will be permanently deleted.",
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/admin/portfolio/${projectId}/photos`, { data: { photo_url: photoUrl } });
          toast.success("Photo removed"); fetchProjects();
        } catch (err) { toast.error("Failed to remove photo"); }
        setConfirmModal(null);
      }
    });
  };

  // Load photo blobs for internal storage paths
  useEffect(() => {
    if (!projects.length) return;
    let cancelled = false;
    const load = async () => {
      const urls = {};
      for (const p of projects) {
        for (const photo of (p.photos || [])) {
          if (cancelled) return;
          if (photo.startsWith("/api/") && !photoUrls[photo]) {
            try {
              const resp = await api.get(photo.replace("/api/", "/"), { responseType: "blob" });
              urls[photo] = URL.createObjectURL(resp.data);
            } catch (err) { /* skip */ }
          }
        }
      }
      if (!cancelled) setPhotoUrls(prev => ({ ...prev, ...urls }));
    };
    load();
    return () => { cancelled = true; };
  }, [projects, api]);

  const getPhotoSrc = (url) => {
    if (!url) return "";
    if (url.startsWith("/api/")) return photoUrls[url] || "";
    return url;
  };

  const getProjectPhotos = (p) => unsavedReorder[p.id] || p.photos || [];

  const handlePhotoDragStart = (e, projectId, fromIndex) => {
    setDragPhoto({ projectId, fromIndex });
    e.dataTransfer.effectAllowed = "move";
  };

  const handlePhotoDragOver = (e, projectId, toIndex) => {
    e.preventDefault();
    if (dragPhoto?.projectId !== projectId) return;
    setDragOverIndex(toIndex);
  };

  const handlePhotoDrop = (e, projectId, toIndex) => {
    e.preventDefault();
    if (!dragPhoto || dragPhoto.projectId !== projectId) return;
    const fromIndex = dragPhoto.fromIndex;
    if (fromIndex === toIndex) { setDragPhoto(null); setDragOverIndex(null); return; }
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    const photos = [...getProjectPhotos(project)];
    const [moved] = photos.splice(fromIndex, 1);
    photos.splice(toIndex, 0, moved);
    setUnsavedReorder(prev => ({ ...prev, [projectId]: photos }));
    // Auto-save to backend
    savePhotoOrder(projectId, photos);
    setDragPhoto(null);
    setDragOverIndex(null);
  };

  const handlePhotoDragEnd = () => { setDragPhoto(null); setDragOverIndex(null); };

  const savePhotoOrder = async (projectId, photos) => {
    try {
      await api.put(`/admin/portfolio/${projectId}/photos/reorder`, { photos });
      toast.success("Photo order saved");
      setUnsavedReorder(prev => { const n = { ...prev }; delete n[projectId]; return n; });
      fetchProjects();
    } catch (err) { toast.error("Failed to save order"); }
  };

  const openPreview = (project, idx = 0) => {
    setPreviewProject(project);
    setPreviewIdx(idx);
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-stone-500">Loading...</div>;

  return (
    <div data-testid="portfolio-module" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-forest-50 rounded-lg flex items-center justify-center">
            <Image className="w-5 h-5 text-forest-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-stone-800">Portfolio Config</h2>
            <p className="text-sm text-stone-500">{projects.length} project{projects.length !== 1 ? "s" : ""} · Top 10 published on site</p>
          </div>
        </div>
        <button data-testid="add-portfolio-btn" onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Plus className="w-4 h-4" /> New Project
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm" data-testid="portfolio-form">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold text-stone-800">{editingProject ? "Edit Project" : "New Project"}</h3>
            <button onClick={resetForm} className="text-stone-400 hover:text-stone-600"><X className="w-5 h-5" /></button>
          </div>
          <BackButton onClick={resetForm} testId="portfolio-form-back" />
          <div className="mt-3" />
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Title *</label>
                <input data-testid="pf-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" required />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Work Type</label>
                <input data-testid="pf-work-type" value={form.work_type} onChange={e => setForm({ ...form, work_type: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="e.g. Mulch Installation" />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Location</label>
                <input data-testid="pf-location" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="e.g. Macon, GA" />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Date</label>
                <input data-testid="pf-date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="e.g. March 2026" />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Category</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm">
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block flex items-center gap-1"><GripVertical className="w-3 h-3" /> Display Priority</label>
                <input data-testid="pf-priority" type="number" value={form.priority} onChange={e => setForm({ ...form, priority: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" min="1" />
                <p className="text-xs text-stone-400 mt-0.5">#1 = first on site. Top 10 are published.</p>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block">Description</label>
              <textarea data-testid="pf-description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                rows={3} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm resize-none" />
            </div>
            <div className="flex gap-3">
              <button type="submit" data-testid="pf-submit" className="flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-5 py-2 rounded-lg text-sm font-medium">
                <Check className="w-4 h-4" /> {editingProject ? "Update" : "Create"}
              </button>
              <button type="button" onClick={resetForm} className="px-5 py-2 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-50">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <div className="bg-white rounded-xl border border-stone-200 p-12 text-center">
          <Image className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <h3 className="font-semibold text-stone-500 mb-1">No projects yet</h3>
          <p className="text-sm text-stone-400">Click "New Project" to add your first portfolio item.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {projects.map((p, idx) => (
            <div key={p.id} data-testid={`portfolio-item-${p.id}`}
              className={`bg-white rounded-xl border overflow-hidden shadow-sm transition-all ${idx < 10 ? "border-forest-200" : "border-stone-200 opacity-70"}`}>
              <div className="flex items-start gap-4 p-5">
                {/* Priority Badge */}
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${idx < 10 ? "bg-forest-100 text-forest-700" : "bg-stone-100 text-stone-500"}`}>
                    #{p.priority}
                  </span>
                  {idx < 10 && <span className="text-[9px] font-medium text-forest-600 uppercase">Live</span>}
                </div>

                {/* Thumbnail */}
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-stone-100 flex-shrink-0 cursor-pointer" onClick={() => p.photos?.length > 0 && openPreview(p, 0)}>
                  {(p.photos?.[0] || p.image_url) ? (
                    <img src={getPhotoSrc(p.photos?.[0] || p.image_url)} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Image className="w-6 h-6 text-stone-300" /></div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-stone-800">{p.title}</h4>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-stone-500">
                    {p.work_type && <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{p.work_type}</span>}
                    {p.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{p.location}</span>}
                    {p.date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{p.date}</span>}
                    <span className="px-1.5 py-0.5 rounded bg-stone-100 capitalize">{p.category}</span>
                  </div>
                  {p.description && <p className="text-xs text-stone-400 mt-1 line-clamp-1">{p.description}</p>}
                </div>

                {/* Photos count + Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-stone-400">{p.photos?.length || 0}/10 photos</span>
                  <input type="number" defaultValue={p.priority} min="1" className="w-14 px-2 py-1 border border-stone-200 rounded text-xs text-center"
                    onBlur={(e) => { const v = Number(e.target.value); if (v !== p.priority) handlePriorityChange(p, v); }}
                    title="Change display priority" />
                  <button onClick={() => handleEdit(p)} title="Edit project" data-testid={`edit-pf-${p.id}`}
                    className="p-1.5 rounded-md hover:bg-stone-100 text-stone-400 hover:text-forest-600 transition-colors">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(p)} title="Delete project" data-testid={`delete-pf-${p.id}`}
                    className="p-1.5 rounded-md hover:bg-red-50 text-stone-400 hover:text-red-600 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Photos Row — Drag & Drop */}
              <div className="px-5 pb-4">
                <div className="flex gap-2 flex-wrap items-center">
                  {getProjectPhotos(p).map((photo, pi) => (
                    <div
                      key={`${p.id}-${pi}`}
                      draggable
                      onDragStart={(e) => handlePhotoDragStart(e, p.id, pi)}
                      onDragOver={(e) => handlePhotoDragOver(e, p.id, pi)}
                      onDrop={(e) => handlePhotoDrop(e, p.id, pi)}
                      onDragEnd={handlePhotoDragEnd}
                      className={`relative w-16 h-16 rounded-lg overflow-hidden bg-stone-100 group cursor-grab active:cursor-grabbing transition-all ${
                        dragPhoto?.projectId === p.id && dragPhoto?.fromIndex === pi
                          ? "opacity-30 scale-90 border-2 border-dashed border-forest-400"
                          : dragOverIndex === pi && dragPhoto?.projectId === p.id
                            ? "ring-2 ring-forest-500 ring-offset-2 scale-105"
                            : "hover:ring-2 hover:ring-forest-300"
                      }`}
                      onClick={(e) => { if (!dragPhoto) openPreview(p, pi); }}
                    >
                      <img src={getPhotoSrc(photo)} alt="" className="w-full h-full object-cover" loading="lazy" />
                      {/* Order number badge */}
                      <span className="absolute top-0.5 left-0.5 bg-black/60 text-white text-[9px] font-bold w-4 h-4 rounded flex items-center justify-center">
                        {pi + 1}
                      </span>
                      {pi === 0 && (
                        <span className="absolute bottom-0 left-0 right-0 bg-forest-600/80 text-white text-[8px] text-center font-bold py-0.5">COVER</span>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); handleDeletePhoto(p.id, photo); }}
                        className="absolute top-0.5 right-0.5 bg-red-500 text-white p-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))}
                  {(getProjectPhotos(p).length) < 10 && (
                    <label className="w-16 h-16 rounded-lg border-2 border-dashed border-stone-300 flex items-center justify-center cursor-pointer hover:border-forest-400 hover:bg-forest-50/50 transition-colors"
                      data-testid={`upload-pf-photo-${p.id}`}>
                      <Upload className="w-5 h-5 text-stone-400" />
                      <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handlePhotoUpload(p.id, e.target.files)} disabled={uploading} />
                    </label>
                  )}
                </div>
                {getProjectPhotos(p).length > 1 && (
                  <p className="text-[10px] text-stone-400 mt-1.5 flex items-center gap-1"><GripVertical className="w-3 h-3" /> Drag photos to reorder. #1 is the cover image.</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Photo Preview Modal */}
      {previewProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="photo-preview-modal"
          onClick={() => setPreviewProject(null)}>
          <div className="relative max-w-4xl w-full mx-4 flex flex-col md:flex-row bg-white rounded-2xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            {/* Image */}
            <div className="relative md:w-3/5 h-[300px] md:h-[500px] bg-stone-900 flex items-center justify-center">
              {getProjectPhotos(previewProject)[previewIdx] && (
                <img src={getPhotoSrc(getProjectPhotos(previewProject)[previewIdx])} alt={previewProject.title}
                  className="w-full h-full object-contain" />
              )}
              {getProjectPhotos(previewProject).length > 1 && (
                <>
                  <button onClick={() => setPreviewIdx(i => (i - 1 + getProjectPhotos(previewProject).length) % getProjectPhotos(previewProject).length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow hover:bg-white transition-colors">
                    <ChevronLeft className="w-5 h-5 text-stone-700" />
                  </button>
                  <button onClick={() => setPreviewIdx(i => (i + 1) % getProjectPhotos(previewProject).length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow hover:bg-white transition-colors">
                    <ChevronRight className="w-5 h-5 text-stone-700" />
                  </button>
                </>
              )}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full">
                {previewIdx + 1} / {getProjectPhotos(previewProject).length || 0}
              </div>
            </div>
            {/* Info Panel */}
            <div className="md:w-2/5 p-6 flex flex-col justify-center">
              <span className="inline-block bg-forest-100 text-forest-600 text-xs uppercase tracking-wider px-3 py-1 rounded-full mb-3 w-fit">{previewProject.category}</span>
              <h3 className="text-xl font-bold text-stone-800 mb-4">{previewProject.title}</h3>
              <div className="space-y-3 text-sm">
                {previewProject.work_type && <div className="flex items-center gap-2 text-stone-600"><Briefcase className="w-4 h-4 text-forest-600" />{previewProject.work_type}</div>}
                {previewProject.location && <div className="flex items-center gap-2 text-stone-600"><MapPin className="w-4 h-4 text-forest-600" />{previewProject.location}</div>}
                {previewProject.date && <div className="flex items-center gap-2 text-stone-600"><Calendar className="w-4 h-4 text-forest-600" />{previewProject.date}</div>}
              </div>
              {previewProject.description && <p className="text-sm text-stone-500 mt-4 leading-relaxed">{previewProject.description}</p>}
              <button onClick={() => setPreviewProject(null)} className="mt-6 px-4 py-2 rounded-lg border border-stone-300 text-sm text-stone-600 hover:bg-stone-50 transition-colors w-fit">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="confirm-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 text-center">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${confirmModal.danger ? "bg-red-50" : "bg-forest-50"}`}>
              {confirmModal.danger ? <AlertTriangle className="w-7 h-7 text-red-500" /> : <Info className="w-7 h-7 text-forest-600" />}
            </div>
            <h3 className="text-lg font-semibold text-stone-800 mb-2">{confirmModal.title}</h3>
            <p className="text-sm text-stone-500 mb-6">{confirmModal.message}</p>
            <div className="flex items-center justify-center gap-3">
              <button onClick={() => setConfirmModal(null)} className="px-5 py-2 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors">Cancel</button>
              <button onClick={confirmModal.onConfirm} className={`px-5 py-2 rounded-lg text-sm font-medium text-white transition-colors ${confirmModal.danger ? "bg-red-600 hover:bg-red-700" : "bg-forest-600 hover:bg-forest-700"}`}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
